import { Injectable, inject, signal } from '@angular/core';
import type {
  CreateShipmentInitialRequest,
  CreateShipmentPaymentRequiredResponse,
  CreateShipmentSuccessResponse,
} from '../../../core/api/shipment/models/create-shipment.model';
import { ShipmentApiService } from '../../../core/api/shipment/shipment-api.service';
import { MonerisCheckoutService } from '../../../core/integrations/moneris/moneris-checkout.service';
import {
  extractCreateShipmentFieldError,
  type CreateShipmentDenyField,
} from '../utils/extract-create-shipment-field-error';

/** Host element id for the Moneris Checkout mount (must match the template). */
export const MONERIS_CHECKOUT_HOST_ID = 'moneris-checkout-host';

export type PaymentFlowResult =
  | { status: 'success'; response: CreateShipmentSuccessResponse }
  | { status: 'failed'; message: string; code?: string; field?: CreateShipmentDenyField }
  | { status: 'cancelled' }
  | { status: 'timeout' };

/**
 * Orchestrates the two-call Shipment Manager flow around hosted Moneris checkout.
 */
@Injectable({ providedIn: 'root' })
export class PaymentFlowService {
  private readonly shipmentApi = inject(ShipmentApiService);
  private readonly monerisCheckout = inject(MonerisCheckoutService);

  /** True while initial create, hosted checkout, or continuation is active. */
  readonly checkoutActive = signal(false);
  /** True while the existing Moneris modal should be visible. */
  readonly modalOpen = signal(false);
  /**
   * True from payment completion until the continuation create call settles.
   * Moneris leaves a bare receipt frame on screen once payment_complete fires,
   * so the modal shows our own progress state instead for that window.
   */
  readonly processing = signal(false);

  private resolveResult: ((result: PaymentFlowResult) => void) | null = null;
  private activeTicket = '';
  private activeFlowId = '';
  private activeInitialRequest: CreateShipmentInitialRequest | null = null;
  private continuationArtifacts: {
    shipmentGenerationToken: string;
    paymentPreloadToken: string;
  } | null = null;
  private flowGeneration = 0;
  private continuationStarted = false;

  /**
   * Starts shipment creation and opens Moneris only when the backend requires payment.
   */
  start(flowId: string, request: CreateShipmentInitialRequest): Promise<PaymentFlowResult> {
    if (this.checkoutActive()) {
      return Promise.resolve({ status: 'cancelled' });
    }

    this.checkoutActive.set(true);
    this.activeFlowId = flowId;
    this.activeInitialRequest = request;
    this.continuationArtifacts = null;
    this.continuationStarted = false;
    const generation = ++this.flowGeneration;

    return new Promise<PaymentFlowResult>((resolve) => {
      this.resolveResult = resolve;
      void this.runInitialCreate(generation);
    });
  }

  /**
   * Cancels the active UI checkout and invalidates late Moneris callbacks.
   */
  cancelFromUi(): void {
    this.flowGeneration += 1;
    this.finish({ status: 'cancelled' });
    this.closeCheckout();
  }

  /**
   * Times out the active checkout and invalidates late Moneris callbacks.
   */
  timeoutFromUi(): void {
    this.flowGeneration += 1;
    this.finish({ status: 'timeout' });
    this.closeCheckout();
  }

  /**
   * Fails closed on a Moneris SDK/gateway error_event and invalidates late callbacks.
   * Logs the raw payload because the user-facing message deliberately omits provider
   * detail, which otherwise leaves gateway and CSP failures undiagnosable.
   */
  private errorFromUi(data: string): void {
    console.error('Moneris checkout error_event', { flowId: this.activeFlowId, data });
    this.flowGeneration += 1;
    this.finish({ status: 'failed', message: '' });
    this.closeCheckout();
  }

  /**
   * Sends the initial attestation request and branches on the safe response status.
   */
  private async runInitialCreate(generation: number): Promise<void> {
    try {
      const request = this.activeInitialRequest;
      if (!request) {
        this.finish({ status: 'cancelled' });
        return;
      }
      const response = await this.shipmentApi.create(this.activeFlowId, request);
      if (!this.isCurrentFlow(generation)) {
        return;
      }
      if (response.status === 'SUCCESS') {
        this.finish({ status: 'success', response });
        return;
      }
      if (response.status === 'FAILED') {
        this.finish({
          status: 'failed',
          message: response.error.message,
          code: response.error.code,
        });
        return;
      }
      await this.openPaymentRequired(response, generation);
    } catch (error) {
      if (this.isCurrentFlow(generation)) {
        this.finish(this.toFailedResult(error));
      }
    }
  }

  /**
   * Maps create HTTP failures to a deny result, surfacing pickupType / dropOffInformation
   * validation messages so Review can interpolate `{{phone}}` instead of collapsing to generic.
   */
  private toFailedResult(error: unknown): Extract<PaymentFlowResult, { status: 'failed' }> {
    const fieldError = extractCreateShipmentFieldError(error);
    if (fieldError) {
      return { status: 'failed', message: fieldError.message, field: fieldError.field };
    }
    return { status: 'failed', message: '' };
  }

  /**
   * Keeps only continuation attestations and opens the hosted checkout ticket.
   */
  private async openPaymentRequired(
    response: CreateShipmentPaymentRequiredResponse,
    generation: number,
  ): Promise<void> {
    const ticket = response.payment.preload.providerPayload.ticket.trim();
    const paymentPreloadToken = response.payment.preload.attestation.trim();
    const shipmentGenerationToken = response.pins.attestation.trim();
    if (!ticket || !paymentPreloadToken || !shipmentGenerationToken) {
      this.finish({ status: 'failed', message: '' });
      return;
    }

    this.activeTicket = ticket;
    this.continuationArtifacts = { paymentPreloadToken, shipmentGenerationToken };
    this.modalOpen.set(true);
    try {
      await this.waitForHostElement(MONERIS_CHECKOUT_HOST_ID);
      if (!this.isCurrentFlow(generation)) {
        return;
      }
      await this.monerisCheckout.startCheckout(ticket, MONERIS_CHECKOUT_HOST_ID, {
        onCancel: () => this.cancelFromUi(),
        onPaymentComplete: (payload) => void this.onPaymentComplete(payload, generation),
        onError: (data) => this.errorFromUi(data),
      });
    } catch {
      if (this.isCurrentFlow(generation)) {
        this.finish({ status: 'failed', message: '' });
        this.closeCheckout();
      }
    }
  }

  /**
   * Extracts the opaque checkout token and submits the complete original request again.
   */
  private async onPaymentComplete(payload: string, generation: number): Promise<void> {
    if (!this.isCurrentFlow(generation) || this.continuationStarted) {
      return;
    }

    const checkoutToken = this.readCheckoutToken(payload);
    const initialRequest = this.activeInitialRequest;
    const artifacts = this.continuationArtifacts;
    if (!checkoutToken || !initialRequest || !artifacts) {
      this.finish({ status: 'failed', message: '' });
      this.closeCheckout();
      return;
    }

    this.continuationStarted = true;
    this.processing.set(true);
    try {
      const response = await this.shipmentApi.create(this.activeFlowId, {
        ...initialRequest,
        ...artifacts,
        checkoutToken,
        paymentSuccessPayload: { shipNow: true },
      });
      if (!this.isCurrentFlow(generation)) {
        return;
      }
      this.closeCheckout();
      if (response.status === 'SUCCESS') {
        this.finish({ status: 'success', response });
      } else if (response.status === 'FAILED') {
        this.finish({
          status: 'failed',
          message: response.error.message,
          code: response.error.code,
        });
      } else {
        this.finish({ status: 'failed', message: '' });
      }
    } catch {
      if (this.isCurrentFlow(generation)) {
        this.closeCheckout();
        this.finish({ status: 'failed', message: '' });
      }
    }
  }

  /**
   * Reads the opaque checkout token from the Moneris payment_complete payload.
   */
  private readCheckoutToken(payload: string): string {
    try {
      const parsed = JSON.parse(payload) as { ticket?: unknown };
      return typeof parsed.ticket === 'string' ? parsed.ticket.trim() : '';
    } catch {
      return '';
    }
  }

  /**
   * Settles once and clears all active checkout artifacts.
   */
  private finish(result: PaymentFlowResult): void {
    const resolve = this.resolveResult;
    this.resolveResult = null;
    this.modalOpen.set(false);
    this.processing.set(false);
    this.checkoutActive.set(false);
    this.activeFlowId = '';
    this.activeInitialRequest = null;
    this.continuationArtifacts = null;
    this.continuationStarted = false;
    resolve?.(result);
  }

  private isCurrentFlow(generation: number): boolean {
    return this.resolveResult !== null && this.flowGeneration === generation;
  }

  private closeCheckout(ticket?: string): void {
    this.monerisCheckout.closeCheckout(ticket ?? (this.activeTicket || undefined));
    this.activeTicket = '';
  }

  /**
   * Waits until Angular renders the Moneris host inside the open modal.
   */
  private async waitForHostElement(hostDivId: string, timeoutMs = 3000): Promise<HTMLElement> {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const host = document.getElementById(hostDivId);
      if (host) {
        return host;
      }
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
    throw new Error(`Moneris checkout host #${hostDivId} was not rendered.`);
  }
}
